import { GLTFLoader, type GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import type { VRM } from "@pixiv/three-vrm";
import { VRMUtils } from "@pixiv/three-vrm";
import * as THREE from "three";
import { createVRMAnimationClip } from "@pixiv/three-vrm-animation";
import { loadMixamoAnimation } from "./loadMixamoAnimation";

type CreateVRMAnimationClipFirstParam = Parameters<typeof createVRMAnimationClip>[0];

export type CachedVRMAsset = { vrm: VRM; gltf: GLTF };
export type RawVRMAsset = {
  buffer: ArrayBuffer;
  blobUrl?: string;
  lastAccessed: number;
  size: number;
};

export type CachedVRMEntry = {
  asset: CachedVRMAsset;
  lastAccessed: number;
  size: number;
};

export type LoadOrGetOptions = {
  onProgress?: (progress: number) => void;
};

export type WarmParsedOptions = {
  concurrency?: number;
};

export type CacheConcurrency = {
  networkConcurrency?: number;
  parseConcurrency?: number;
};

export type InitOnceOptions = {
  urls: string[];
  loader: GLTFLoader;
  targetUrl?: string;
  networkConcurrency?: number;
  parseConcurrency?: number;
};

// Two-level cache: raw binary + parsed gltf/vrm
const rawCache = new Map<string, RawVRMAsset>();
const gltfCache = new Map<string, CachedVRMEntry>();

// Dedupe map for concurrent parse/load requests
const loadPromises = new Map<string, Promise<CachedVRMAsset | null>>();
const rawPromises = new Map<string, Promise<RawVRMAsset>>();
let initOncePromise: Promise<void> | null = null;
// parseTimingMsByUrl removed: timing measurements were logging durations.

type QueueTask<T> = {
  run: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
};

const createQueue = (initialConcurrency: number) => {
  let concurrency = Math.max(1, initialConcurrency);
  let running = 0;
  const pending: QueueTask<unknown>[] = [];

  const pump = () => {
    while (running < concurrency && pending.length > 0) {
      const task = pending.shift();
      if (!task) break;

      running += 1;
      task
        .run()
        .then(task.resolve)
        .catch(task.reject)
        .finally(() => {
          running = Math.max(0, running - 1);
          pump();
        });
    }
  };

  const enqueue = <T>(run: () => Promise<T>): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      pending.push({ run, resolve, reject } as QueueTask<unknown>);
      pump();
    });
  };

  const setConcurrency = (next: number) => {
    concurrency = Math.max(1, next);
    pump();
  };

  const getState = () => ({ concurrency, running, pending: pending.length });

  return {
    enqueue,
    setConcurrency,
    getState,
  };
};

const networkQueue = createQueue(4);
const parseQueue = createQueue(4);

// AnimationClip cache (keyed by `${vrmUrl}::${animationUrl}`)
const clipCache = new Map<string, { clip: THREE.AnimationClip; lastAccessed: number }>();
const clipPromises = new Map<string, Promise<THREE.AnimationClip | null>>();

const getClipKey = (vrmUrl: string, animUrl: string) => `${vrmUrl}::${animUrl}`;

const getFileExtension = (path: string) => {
  const i = path.lastIndexOf(".");
  if (i === -1) return "";
  return path.slice(i + 1).toLowerCase();
};

export const getClip = (vrmUrl: string, animationUrl: string): THREE.AnimationClip | undefined => {
  const key = getClipKey(vrmUrl, animationUrl);
  const entry = clipCache.get(key);
  if (!entry) return undefined;
  entry.lastAccessed = Date.now();
  return entry.clip;
};

export const setClip = (vrmUrl: string, animationUrl: string, clip: THREE.AnimationClip) => {
  const key = getClipKey(vrmUrl, animationUrl);
  clipCache.set(key, { clip, lastAccessed: Date.now() });
};

/**
 * Create (and cache) an AnimationClip for the given VRM + animation resource.
 * If `vrmInstance` is provided it will be used; otherwise the cached VRM for `vrmUrl` is used.
 * This operation is throttled via `parseQueue` to avoid CPU spikes.
 */

export const createClipIfMissing = (
  vrmUrl: string,
  animationUrl: string,
  loader: GLTFLoader,
  vrmInstance?: VRM
): Promise<THREE.AnimationClip | null> => {
  const key = getClipKey(vrmUrl, animationUrl);
  const existing = clipCache.get(key);
  if (existing) return Promise.resolve(existing.clip);

  const existingPromise = clipPromises.get(key);
  if (existingPromise) return existingPromise;

  const p = parseQueue
    .enqueue(async () => {
      const ext = getFileExtension(animationUrl);

      if (ext === "vrma") {
        const loadPath = animationUrl.slice(0, -5);
        return await new Promise<THREE.AnimationClip | null>((resolve, reject) => {
          loader.load(
            loadPath,
            (gltf: GLTF) => {
              try {
                const vrmAnimations =
                  (gltf.userData as unknown as { vrmAnimations?: unknown[] }).vrmAnimations ?? null;
                if (!vrmAnimations || vrmAnimations.length === 0) {
                  resolve(null);
                  return;
                }
                const vrmAnimation = vrmAnimations[0];
                const vrm = vrmInstance ?? gltfCache.get(vrmUrl)?.asset.vrm;
                if (!vrm) {
                  // VRM instance not available; cannot create clip here.
                  resolve(null);
                  return;
                }
                const clip = createVRMAnimationClip(
                  vrmAnimation as CreateVRMAnimationClipFirstParam,
                  vrm as VRM
                );
                clipCache.set(key, { clip, lastAccessed: Date.now() });
                resolve(clip);
              } catch (e) {
                reject(e);
              }
            },
            undefined,
            (err) => reject(err)
          );
        });
      }

      if (ext === "fbx") {
        const loadPath = animationUrl.slice(0, -4);
        const vrm = vrmInstance ?? gltfCache.get(vrmUrl)?.asset.vrm;
        if (!vrm) return null;

        const clip = await loadMixamoAnimation(loadPath, vrm as VRM);
        clipCache.set(key, { clip, lastAccessed: Date.now() });
        return clip;
      }

      return null;
    })
    .finally(() => {
      clipPromises.delete(key);
    });

  clipPromises.set(key, p);
  return p;
};

export const warmClipsForVRM = async (
  vrmUrl: string,
  animationUrls: string[],
  loader: GLTFLoader,
  options?: { concurrency?: number }
) => {
  const workerCount = Math.max(1, options?.concurrency ?? 1);
  const queue = [...new Set(animationUrls)];

  const worker = async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next) return;
      try {
        await createClipIfMissing(vrmUrl, next, loader);
      } catch {
        // ignore warming errors
      }
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
};

const touchRaw = (url: string) => {
  const entry = rawCache.get(url);
  if (!entry) return;
  entry.lastAccessed = Date.now();
};

const touchGltf = (url: string) => {
  const entry = gltfCache.get(url);
  if (!entry) return;
  entry.lastAccessed = Date.now();
};

const createBlobUrlFromBuffer = (buffer: ArrayBuffer) => {
  const blob = new Blob([buffer], { type: "model/gltf-binary" });
  return URL.createObjectURL(blob);
};
const fetchRawBuffer = async (
  url: string,
  onProgress?: (xhr: { loaded: number; total?: number }) => void
  // eslint-disable-next-line complexity
): Promise<RawVRMAsset> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch VRM: ${response.status} ${response.statusText}`);
  }

  const contentLengthHeader = response.headers.get("content-length");
  const total = contentLengthHeader ? parseInt(contentLengthHeader, 10) : undefined;

  if (!response.body || typeof response.body.getReader !== "function") {
    const buffer = await response.arrayBuffer();
    try {
      onProgress?.({ loaded: buffer.byteLength, total });
    } catch {
      /* ignore */
    }
    const blobUrl = createBlobUrlFromBuffer(buffer);
    const now = Date.now();
    return {
      buffer,
      blobUrl,
      lastAccessed: now,
      size: buffer.byteLength,
    };
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.length;
      try {
        onProgress?.({ loaded, total });
      } catch {
        // ignore
      }
    }
  }

  const buffer = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    buffer.set(chunk, offset);
    offset += chunk.length;
  }
  const arrayBuffer = buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
  const blobUrl = createBlobUrlFromBuffer(arrayBuffer);
  const now = Date.now();
  return {
    buffer: arrayBuffer,
    blobUrl,
    lastAccessed: now,
    size: arrayBuffer.byteLength,
  };
};

const parseFromBuffer = (
  buffer: ArrayBuffer,
  loader: GLTFLoader
): Promise<CachedVRMAsset | null> => {
  return new Promise<CachedVRMAsset | null>((resolve, reject) => {
    loader.parse(
      buffer,
      "",
      (gltf) => {
        try {
          const vrm = gltf.userData.vrm as VRM | null;
          if (!vrm) {
            resolve(null);
            return;
          }
          try {
            VRMUtils.rotateVRM0(vrm);
          } catch {
            // not fatal
          }
          resolve({ vrm, gltf });
        } catch (err) {
          reject(err);
        }
      },
      (err) => {
        reject(err);
      }
    );
  });
};

const fetchRawOrGet = async (
  url: string,
  onProgress?: (xhr: { loaded: number; total?: number }) => void
): Promise<RawVRMAsset> => {
  const existing = rawCache.get(url);
  if (existing) {
    touchRaw(url);
    try {
      onProgress?.({ loaded: existing.size, total: existing.size });
    } catch {
      // ignore
    }
    return existing;
  }

  const existingPromise = rawPromises.get(url);
  if (existingPromise) {
    return existingPromise;
  }

  const p = networkQueue
    .enqueue(() => fetchRawBuffer(url, onProgress))
    .then((raw) => {
      rawCache.set(url, raw);
      return raw;
    })
    .finally(() => {
      rawPromises.delete(url);
    });

  rawPromises.set(url, p);
  return p;
};

export const setConcurrency = ({ networkConcurrency, parseConcurrency }: CacheConcurrency) => {
  if (typeof networkConcurrency === "number") {
    networkQueue.setConcurrency(networkConcurrency);
  }
  if (typeof parseConcurrency === "number") {
    parseQueue.setConcurrency(parseConcurrency);
  }
};

export const getConcurrencyState = () => ({
  network: networkQueue.getState(),
  parse: parseQueue.getState(),
});

export const has = (url: string) => gltfCache.has(url);
export const hasRaw = (url: string) => rawCache.has(url);

export const get = (url: string): CachedVRMAsset | undefined => {
  const entry = gltfCache.get(url);
  if (!entry) return undefined;
  touchGltf(url);
  return entry.asset;
};

export const getRaw = (url: string): RawVRMAsset | undefined => {
  const entry = rawCache.get(url);
  if (!entry) return undefined;
  touchRaw(url);
  return entry;
};

export const set = (url: string, asset: CachedVRMAsset) => {
  gltfCache.set(url, {
    asset,
    lastAccessed: Date.now(),
    // Keep coarse size value. Real GPU memory usage is larger and renderer-dependent.
    size: 0,
  });
};

export const setRaw = (url: string, raw: RawVRMAsset) => {
  rawCache.set(url, raw);
};

export const prefetchRaw = async (url: string): Promise<RawVRMAsset> => {
  return fetchRawOrGet(url);
};

export const prefetchRawAll = async (urls: string[], concurrency: number = 3): Promise<void> => {
  const workerCount = Math.max(1, concurrency);
  const queue = [...new Set(urls)];

  const worker = async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next) return;
      try {
        await fetchRawOrGet(next);
      } catch {
        // keep going; caller can inspect misses via hasRaw
      }
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
};

export const warmParsedAll = async (
  urls: string[],
  loader: GLTFLoader,
  options?: WarmParsedOptions
): Promise<void> => {
  const workerCount = Math.max(1, options?.concurrency ?? 1);
  const queue = [...new Set(urls)];

  const worker = async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next) return;
      try {
        await parseRawWithLoader(next, loader);
      } catch {
        // keep going; caller can inspect cache state via has/hasRaw
      }
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
};

/**
 * Parse raw buffer with a provided loader and return a fresh parsed asset.
 * This does NOT store the result in the gltfCache and is useful when callers
 * need an independent scene/VRM instance (avoid sharing the cached instance).
 */
export type ParseRawOptions = {
  cache?: boolean; // if true, store parsed result in gltfCache for reuse
};

export const parseRawWithLoader = async (
  url: string,
  loader: GLTFLoader,
  onProgress?: (xhr: { loaded: number; total?: number }) => void,
  options?: ParseRawOptions
): Promise<CachedVRMAsset | null> => {
  const raw = await fetchRawOrGet(url, onProgress);
  if (!raw) return null;
  const asset = await parseQueue.enqueue(() => parseFromBuffer(raw.buffer, loader));

  try {
    onProgress?.({ loaded: raw.size, total: raw.size });
  } catch {
    // ignore
  }

  // Optionally cache the parsed asset so future callers can reuse it via loadOrGet/get
  if (options?.cache && asset) {
    gltfCache.set(url, {
      asset,
      lastAccessed: Date.now(),
      size: raw.size,
    });
  }

  return asset;
};

/**
 * App-start one-time initializer.
 * - prefetch raw buffers for all URLs
 * - parse target URL once (optional)
 * Promise-guarded so repeated callers share the same run.
 */
export const initializeOnce = async (options: InitOnceOptions): Promise<void> => {
  if (initOncePromise) return initOncePromise;

  initOncePromise = (async () => {
    const uniqueUrls = [...new Set(options.urls)].filter(Boolean);
    if (
      typeof options.networkConcurrency === "number" ||
      typeof options.parseConcurrency === "number"
    ) {
      setConcurrency({
        networkConcurrency: options.networkConcurrency,
        parseConcurrency: options.parseConcurrency,
      });
    }

    if (uniqueUrls.length > 0) {
      await prefetchRawAll(uniqueUrls, options.networkConcurrency ?? 3);
    }

    const target = options.targetUrl || uniqueUrls[0];
    if (target) {
      // Parse and cache the target so subsequent loads can reuse the parsed asset.
      await parseRawWithLoader(target, options.loader, undefined, { cache: true });
    }
  })().catch((error) => {
    // allow retry if startup init failed once
    initOncePromise = null;
    throw error;
  });

  return initOncePromise;
};

export const loadOrGet = (
  url: string,
  loader: GLTFLoader,
  options?: LoadOrGetOptions
): Promise<CachedVRMAsset | null> => {
  const onProgress = options?.onProgress;

  const cached = gltfCache.get(url);
  if (cached) {
    touchGltf(url);
    onProgress?.(100);
    return Promise.resolve(cached.asset);
  }

  const existing = loadPromises.get(url);
  if (existing) {
    return existing.then((asset) => {
      if (asset) onProgress?.(100);
      return asset;
    });
  }

  const p = (async () => {
    // Ensure raw is present first (download/prebuffer)
    const raw = await fetchRawOrGet(url);

    // Parse should be throttled separately from network
    onProgress?.(60);
    const asset = await parseQueue.enqueue(() => parseFromBuffer(raw.buffer, loader));
    if (!asset) {
      return null;
    }

    gltfCache.set(url, {
      asset,
      lastAccessed: Date.now(),
      size: raw.size,
    });
    onProgress?.(100);

    return asset;
  })().finally(() => {
    loadPromises.delete(url);
  });

  loadPromises.set(url, p);
  return p;
};

export const clear = () => {
  for (const raw of rawCache.values()) {
    if (raw.blobUrl) {
      URL.revokeObjectURL(raw.blobUrl);
    }
  }
  rawCache.clear();
  gltfCache.clear();
  loadPromises.clear();
  rawPromises.clear();
};

export default {
  has,
  hasRaw,
  get,
  getRaw,
  set,
  setRaw,
  setConcurrency,
  getConcurrencyState,
  prefetchRaw,
  prefetchRawAll,
  initializeOnce,
  warmParsedAll,
  loadOrGet,
  parseRawWithLoader,
  getClip,
  setClip,
  createClipIfMissing,
  warmClipsForVRM,
  clear,
};
